import React, { FormEvent, useEffect, useId, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import axiosInstance from 'config/axiosInstance';
import { discardResendRecord, readResendRecord, writeResendRecord } from 'config/createRecordStore';
import { REQUEST_ID_REUSE_MS } from 'config/idempotentCreate';
import { getSessionIdentity } from 'config/session';
import { formatBusinessDateTime } from 'config/time';
import { newRequestId } from 'components/Driver/registerDriver';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import { useSaveError } from 'reusable/useSaveError';

interface ResendInvitationProps {
  id?: string;
}

/**
 * EV-284（RV-359）。結果が分からない失敗か（同じ X-Request-ID で送り直す）。応答が無い・5xx・408・429・401・403・
 * 409 idempotency_in_progress は、前の要求がサーバーで確定したか分からない。サーバーは同じ値の再送に前回の
 * 応答を返し、失敗は残さない（同じ値で改めて再送する）ので、迷うときは同じ値を使う。
 * EV-346。403（権限）は、サーバーが冪等の記録を見る前（IdempotentMixin.initial）に返すので、前の再送が
 * 確定したかは分からない（作成の操作の establishesOutcome と同じ考え）。
 */
const keepsRequestId = (error: any): boolean => {
  const response = error?.response;
  if (!response) return true;
  const { status } = response;
  if (status >= 500 || status === 408 || status === 429 || status === 401 || status === 403) return true;
  return status === 409 && response.data?.code === 'idempotency_in_progress';
};

/**
 * BE-20-AC3（IR-AC1〜3、2026-10-11 PM 承認の文言）。運転者の招待を再送する。
 *
 * 初回のパスワードを未設定（must_change_password が true）の運転者にだけボタンを出す。招待の
 * 初期パスワードは7日で切れ、再送すると新しい初期パスワードが届き、前の招待のパスワードは
 * 使えなくなる。確認の後に POST /v1/drivers/{id}/resend_invitation/ を送る（X-Request-ID 付き。EV-284）。
 */
const ResendInvitation: React.FC<ResendInvitationProps> = ({ id }) => {
  const { t } = useTranslation('driver');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const [pending, setPending] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const headingId = useId();
  const close = () => setIsOpen(false);
  const dialogRef = useModalDialog(isOpen, close);
  // IR-AC5（2026-10-11 PM 承認の案A）。再送の成功の完了の表示。null は閉じている。
  // 中身は会社の時間帯の期限（YYYY-MM-DD HH:mm）。期限が無い・読めないときは空文字（1文目だけを出す）。
  const [doneExpiry, setDoneExpiry] = useState<string | null>(null);
  const doneTitleId = useId();
  const closeDone = () => setDoneExpiry(null);
  const doneRef = useModalDialog(doneExpiry !== null, closeDone);
  // EV-284（RV-359）。再送の操作の X-Request-ID。結果が分からない失敗の後にもう一度押したときは同じ値で
  // 送る（応答が失われた後の再試行で、新しい初期パスワードを作り直させない）。成功・結果の分かる失敗の
  // 後は捨てる。PR #266 の Codex の指摘（4270014211）。値はこのタブの sessionStorage に運転者ごとに残し、
  // 再読み込み・画面の移動の後も使う（作成の操作の RV-130 と同じ考え）。利用者の印が変わったとき、作って
  // から23時間（サーバーの記録の期限の1時間前）を過ぎたときは使わない。
  const requestIdFor = (driver?: string): string => {
    const held = driver ? readResendRecord(driver) : null;
    if (
      held &&
      typeof held.requestId === 'string' &&
      typeof held.savedAt === 'number' &&
      held.session === getSessionIdentity() &&
      Date.now() >= held.savedAt &&
      Date.now() - held.savedAt < REQUEST_ID_REUSE_MS
    ) {
      return held.requestId;
    }
    const requestId = newRequestId();
    if (driver) writeResendRecord(driver, { requestId, session: getSessionIdentity(), savedAt: Date.now() });
    return requestId;
  };
  const discardRequestId = (driver: string | undefined, requestId: string) => {
    if (driver && readResendRecord(driver)?.requestId === requestId) discardResendRecord(driver);
  };

  useEffect(() => {
    let active = true;
    if (!id) return;
    axiosInstance
      .get(`/v1/drivers/${id}/`)
      .then((response) => {
        if (active) setPending(response.data?.must_change_password === true);
      })
      .catch((error) => console.error('Failed to fetch driver', error));
    return () => {
      active = false;
    };
  }, [id]);

  // 再送できなくなった後も、理由の表示（S3 など）は閉じるまで残す。
  if (!pending) return saveErrorPopup;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const requestId = requestIdFor(id);
    try {
      const response = await axiosInstance.post(`/v1/drivers/${id}/resend_invitation/`, undefined, {
        headers: { 'X-Request-ID': requestId },
      });
      discardRequestId(id, requestId);
      setIsOpen(false);
      setDoneExpiry(formatBusinessDateTime(response?.data?.invitation_expires_at, ''));
    } catch (error: any) {
      if (!keepsRequestId(error)) discardRequestId(id, requestId);
      // 本人がすでにパスワードを設定した（S3）。ほかの失敗は保存の失敗の表示で理由を出す。
      if (error?.response?.data?.code === 'invitation_already_accepted') {
        showMessages([t('resend-invitation.already-accepted')]);
        setPending(false);
        setIsOpen(false);
      } else {
        showSaveError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        className="inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300 h-10 px-4"
        onClick={() => setIsOpen(true)}
      >
        {t('resend-invitation.button')}
      </button>
      {isOpen ? inModalLayer(
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby={headingId}
              ref={dialogRef}
              tabIndex={-1}
            >
              <h3 className="text-subtitle-1 font-medium text-neutral/500 mt-4 mx-6" id={headingId}>
                {t('resend-invitation.button')}
              </h3>
              <hr className="border-neutral/50 mt-4 w-full" />
              <form onSubmit={handleSubmit}>
                <p className="m-4 sm:m-6 text-body-2">{t('resend-invitation.confirm')}</p>
                <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center items-center rounded-md border bg-white text-subtitle-2 text-neutral/100 disabled:opacity-60"
                    onClick={close}
                  >
                    {t('ui.cancel', { ns: 'common' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                  >
                    {t('resend-invitation.button')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
      {doneExpiry !== null ? inModalLayer(
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div
            className="flex flex-col py-4 bg-white rounded-md shadow-lg w-1/3"
            role="dialog"
            aria-modal="true"
            aria-labelledby={doneTitleId}
            ref={doneRef}
            tabIndex={-1}
          >
            <div className="flex justify-start px-4 pb-4">
              <h4 id={doneTitleId} className="text-title">{t('ui.success', { ns: 'common' })}</h4>
            </div>
            <div className="border-y w-full flex flex-col items-center justify-center py-4">
              <CheckIcon className="bg-[#67B926] rounded-full text-white h-24 p-4" />
              <p className="py-4 px-4 text-center">
                {doneExpiry
                  ? t('resend-invitation.done', { date: doneExpiry })
                  : t('resend-invitation.done-without-date')}
              </p>
            </div>
            <div className="flex justify-end mt-4 w-full px-4">
              <button type="button" className="bg-primary/300 text-white w-full py-2 rounded-md" onClick={closeDone}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {saveErrorPopup}
    </div>
  );
};

export default ResendInvitation;
